import { fetchAllJsonCached } from '../../fetch-all-json';
import { createOcrRateChart } from './chart';

function element (documentRoot, tag, value) {
  const node = documentRoot.createElement(tag);
  if (value !== undefined) node.textContent = value;
  return node;
}

function count (value) {
  return value === null || value === undefined ? '—' : Number(value).toLocaleString('en-US');
}

export function renderOcrElectionSummary (container, metrics) {
  const documentRoot = container.ownerDocument;
  container.classList.add('ocr-election-summary');
  const title = element(documentRoot, 'h2', 'Election keywords in captured screenshots');
  const note = element(documentRoot, 'p', 'These are all processed screenshot OCR records, not people or unique exposures. A keyword match does not prove a displayed voting reminder or bias. Match rates use classified records only; stored classification rules can vary over time.');
  container.replaceChildren(title, note);
  const sources = Object.entries(metrics?.sources || {});
  if (sources.length === 0) {
    const status = element(documentRoot, 'p', 'OCR source metrics are not available yet.');
    status.setAttribute('role', 'status');
    container.append(status);
    return;
  }
  container.append(createOcrRateChart(documentRoot, sources));
  const region = element(documentRoot, 'div');
  region.className = 'ocr-election-table-scroll';
  region.setAttribute('role', 'region');
  region.setAttribute('aria-label', 'Screenshot OCR source coverage');
  region.tabIndex = 0;
  const table = element(documentRoot, 'table');
  table.append(element(documentRoot, 'caption', 'All processed OCR observations by source'));
  const head = element(documentRoot, 'thead');
  const headers = element(documentRoot, 'tr');
  for (const label of ['Source', 'OCR records', 'Classified records', 'Keyword matches', 'Match rate', 'Status']) {
    const cell = element(documentRoot, 'th', label);
    cell.setAttribute('scope', 'col');
    headers.append(cell);
  }
  head.append(headers);
  table.append(head);
  const body = element(documentRoot, 'tbody');
  for (const [key, source] of sources) {
    const row = element(documentRoot, 'tr');
    row.dataset.source = key;
    const label = element(documentRoot, 'th', source.label);
    label.setAttribute('scope', 'row');
    row.append(label);
    const available = ['available', 'empty'].includes(source.status);
    const percentage = source.match_percentage;
    const rate = available && percentage !== null && percentage !== undefined ? `${Number(percentage).toFixed(1)}%` : '—';
    const status = { available: 'Available', empty: 'No observations', unavailable: 'Unavailable' }[source.status] || 'Unavailable';
    for (const value of [count(available ? source.total_records : null), count(available ? source.classified_records : null), count(available ? source.matched_records : null), rate, status]) {
      row.append(element(documentRoot, 'td', value));
    }
    body.append(row);
  }
  table.append(body);
  region.append(table);
  container.append(region);
  const unclassified = sources.reduce((total, [, source]) => total + (source.unclassified_records || 0), 0);
  container.append(element(documentRoot, 'p', `Among available sources, ${count(unclassified)} records have no valid stored classification and are excluded from the match-rate denominator.`));
  const unattributed = metrics.unattributed_homepage_records;
  container.append(element(documentRoot, 'p', unattributed === null || unattributed === undefined
    ? 'Unattributed homepage counts are currently unavailable.'
    : `${count(unattributed)} homepage OCR records have no recognized engine and are excluded from the engine rows.`));
  if (metrics.queried_at) {
    const date = new Date(metrics.queried_at);
    if (!Number.isNaN(date.getTime())) {
      container.append(element(documentRoot, 'p', `Counts checked ${date.toLocaleString()}. This is query time, not capture time.`));
    }
  }
}

export async function initOcrElectionSummary (load = fetchAllJsonCached, documentRoot = document) {
  const container = documentRoot.getElementById('ocr-election-summary');
  if (!container) return;
  try {
    const value = await load();
    renderOcrElectionSummary(container, value?.ocr_election_metrics);
  } catch {
    renderOcrElectionSummary(container, null);
  }
}
